import {useMemo, useState} from 'react';
import {CareTaskComplementary} from '../backend/interfaces/care-task-complementary';
import {EntityUser} from '../backend/responses/entity-user';
import {useDailyCareLogFilters} from '../contexts/dailycare-log-filters/context';
import {DailyCareLogFilters} from '../contexts/dailycare-log-filters/types';

export const useFilteredTasks = ({
  tasks,
  seniors,
}: {
  tasks: CareTaskComplementary[] | undefined;
  seniors?: Map<string, EntityUser>;
}) => {
  const {tasksFilters} = useDailyCareLogFilters();
  // Start with the initial state from context, so that when we switch tabs we can keep the previously applied filters
  const [activeFilters, setActiveFilters] = useState<DailyCareLogFilters>(tasksFilters);

  const filteredTasks = useMemo(() => {
    const {activitiesGroupIds, socialResponseIds} = activeFilters;

    if (!tasks) {
      return undefined;
    }

    // Start with all unfiltered tasks
    let result = [...tasks];

    // If any activity group id has been selected, filter the tasks by activityGroupId
    if (activitiesGroupIds && activitiesGroupIds.length > 0) {
      result = result.filter(({careTask}) =>
        activitiesGroupIds.includes(careTask.activitiesGroupId)
      );
    }

    // If any social response has been selected, intersect the previous filter with the social responses
    if (socialResponseIds.length > 0) {
      result = result.filter(({careTask}) => {
        const taskSenior = seniors?.get(careTask.userId);
        const seniorSocialResponseIds = taskSenior?.affiliation.socialResponses;

        return seniorSocialResponseIds?.some((id) => socialResponseIds.includes(id));
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, seniors, activeFilters]);

  return {
    filteredTasks,
    activeFilters,
    saveActiveFilters: setActiveFilters,
  };
};
